export const catalogCategorySlugs = [
  'co2-natural-refrigerant',
  'energy-solution',
  'mechanical-hvac',
  'refrigeration-system',
] as const;

export type CatalogCategorySlug = string;

const createCatalogCategoryPath = (categorySlug: CatalogCategorySlug) => {
  return `/${categorySlug}`;
};

const stringifyRouteParam = (value: number | string) => {
  return String(value);
};

export const routePaths = {
  aboutGreeting: '/about/greeting',
  aboutHistory: '/about/history',
  aboutCertification: '/about/certification',
  aboutOrganization: '/about/organization',
  aboutLocation: '/about/location',
  home: '/',
  co2NaturalRefrigerant: createCatalogCategoryPath('co2-natural-refrigerant'),
  energySolution: createCatalogCategoryPath('energy-solution'),
  mechanicalHvac: createCatalogCategoryPath('mechanical-hvac'),
  refrigerationSystem: createCatalogCategoryPath('refrigeration-system'),
  catalogCategory: createCatalogCategoryPath,
  catalogDetail: (itemSlug: string) => {
    return `/catalog-items/${stringifyRouteParam(itemSlug)}`;
  },
  constructionCases: '/construction-cases',
  constructionCaseDetail: (constructionCaseId: number | string) =>
    `/construction-cases/${stringifyRouteParam(constructionCaseId)}`,
  mediaCoverages: '/media-coverages',
  projectRecords: '/project-records',
  supportMaterials: '/support-materials',
  supportMaterialDetail: (supportMaterialId: number | string) =>
    `/support-materials/${stringifyRouteParam(supportMaterialId)}`,
  webCatalogs: '/web-catalogs',
  webCatalogDetail: (webCatalogId: number | string) =>
    `/web-catalogs/${stringifyRouteParam(webCatalogId)}`,
  promotionalVideos: '/promotional-videos',
  promotionalVideoDetail: (promotionalVideoId: number | string) =>
    `/promotional-videos/${stringifyRouteParam(promotionalVideoId)}`,
  admin: '/admin',
  adminLogin: '/admin/login',
  adminHistoryEntries: '/admin/history-entries',
  adminHistoryEntryCreate: '/admin/history-entries/new',
  adminHistoryEntryEdit: (historyEntryId: number | string) =>
    `/admin/history-entries/${stringifyRouteParam(historyEntryId)}/edit`,
  adminOrganization: '/admin/organization',
  adminHomeHeroText: '/admin/home-hero',
  adminHomeFeaturedEquipments: '/admin/home-featured-equipments',
  adminHomeFeaturedEquipmentCreate: '/admin/home-featured-equipments/new',
  adminHomeFeaturedEquipmentEdit: (homeFeaturedEquipmentId: number | string) =>
    `/admin/home-featured-equipments/${stringifyRouteParam(homeFeaturedEquipmentId)}/edit`,
  adminMediaCoverages: '/admin/media-coverages',
  adminMediaCoverageCreate: '/admin/media-coverages/new',
  adminMediaCoverageEdit: (mediaCoverageId: number | string) =>
    `/admin/media-coverages/${stringifyRouteParam(mediaCoverageId)}/edit`,
  adminCertificationAssets: '/admin/certification-assets',
  adminCertificationAssetCreate: '/admin/certification-assets/new',
  adminCertificationAssetEdit: (certificationAssetId: number | string) =>
    `/admin/certification-assets/${stringifyRouteParam(certificationAssetId)}/edit`,
  adminCatalogItemsRoot: '/admin/catalog-items',
  adminCatalogCategories: '/admin/catalog-categories',
  adminCatalogItems: (categorySlug: CatalogCategorySlug) => `/admin/catalog-items/${categorySlug}`,
  adminCatalogItemCreate: (categorySlug: CatalogCategorySlug) =>
    `/admin/catalog-items/${categorySlug}/new`,
  adminCatalogItemEdit: (categorySlug: CatalogCategorySlug, catalogItemId: number | string) =>
    `/admin/catalog-items/${categorySlug}/${stringifyRouteParam(catalogItemId)}/edit`,
  adminPopups: '/admin/popups',
  adminPopupCreate: '/admin/popups/new',
  adminPopupEdit: (popupId: number | string) =>
    `/admin/popups/${stringifyRouteParam(popupId)}/edit`,
  adminConstructionCases: '/admin/construction-cases',
  adminConstructionCaseCreate: '/admin/construction-cases/new',
  adminConstructionCaseEdit: (constructionCaseId: number | string) =>
    `/admin/construction-cases/${stringifyRouteParam(constructionCaseId)}/edit`,
  adminProjectRecords: '/admin/project-records',
  adminProjectRecordCreate: '/admin/project-records/new',
  adminProjectRecordEdit: (projectRecordId: number | string) =>
    `/admin/project-records/${stringifyRouteParam(projectRecordId)}/edit`,
  adminSupportMaterials: '/admin/support-materials',
  adminSupportMaterialCreate: '/admin/support-materials/new',
  adminSupportMaterialEdit: (supportMaterialId: number | string) =>
    `/admin/support-materials/${stringifyRouteParam(supportMaterialId)}/edit`,
  adminWebCatalogs: '/admin/web-catalogs',
  adminWebCatalogCreate: '/admin/web-catalogs/new',
  adminWebCatalogEdit: (webCatalogId: number | string) =>
    `/admin/web-catalogs/${stringifyRouteParam(webCatalogId)}/edit`,
  adminPromoVideos: '/admin/promo-videos',
  adminPromoVideoCreate: '/admin/promo-videos/new',
  adminPromoVideoEdit: (promoVideoId: number | string) =>
    `/admin/promo-videos/${stringifyRouteParam(promoVideoId)}/edit`,
  adminInquiries: '/admin/inquiries',
  adminInquiryDetail: (inquiryId: number | string) =>
    `/admin/inquiries/${stringifyRouteParam(inquiryId)}`,
} as const;

export const catalogCategoryPaths = [
  routePaths.co2NaturalRefrigerant,
  routePaths.energySolution,
  routePaths.mechanicalHvac,
  routePaths.refrigerationSystem,
] as const;

export const isCatalogCategoryPath = (pathname: string) => {
  return catalogCategoryPaths.some((path) => path === pathname);
};

export const isCatalogDetailPath = (pathname: string) => {
  return /^\/catalog-items\/[^/]+$/.test(pathname);
};

export const isCatalogRoute = (pathname: string) => {
  return isCatalogCategoryPath(pathname) || isCatalogDetailPath(pathname);
};
